const baseUrl = (
  import.meta.env.VITE_API_URL || "http://localhost:8787"
).replace(/\/$/, "")

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
  }
}

export type StaffUser = {
  id: string
  name: string
  email: string
  role: "officer" | "admin" | "super_admin"
  twoFactorEnabled?: boolean
  mfaRequired?: boolean
}

export const adminStaffQueryKey = ["admin-me"] as const

export type AdminBootstrap<TDashboard = Record<string, unknown>> = {
  staff: StaffUser
  dashboard: TDashboard | null
  loadedAt: string
}

export function adminBootstrapQueryOptions(includeDashboard = false) {
  return {
    queryKey: [...adminStaffQueryKey, includeDashboard] as const,
    queryFn: () =>
      api<AdminBootstrap>(
        `/admin/bootstrap?includeDashboard=${includeDashboard ? "true" : "false"}`
      ),
    select: (result: AdminBootstrap) => result,
    retry: false,
    staleTime: 5 * 60_000,
  } as const
}

const pendingGetRequests = new Map<string, Promise<unknown>>()

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase()
  const requestKey =
    method === "GET" && !init.signal ? `${baseUrl}/api/v1${path}` : null
  const pending = requestKey ? pendingGetRequests.get(requestKey) : undefined
  if (pending) return pending as Promise<T>

  const request = fetch(`${baseUrl}/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...init.headers },
  }).then(async (response) => {
    const payload = (await response.json().catch(() => null)) as {
      data?: T
      message?: string
      error?: { message?: string }
    } | null
    if (!response.ok)
      throw new ApiError(
        payload?.message ??
          payload?.error?.message ??
          "The request could not be completed.",
        response.status
      )
    return payload?.data as T
  })

  if (requestKey) {
    pendingGetRequests.set(requestKey, request)
    const clear = () => {
      if (pendingGetRequests.get(requestKey) === request)
        pendingGetRequests.delete(requestKey)
    }
    void request.then(clear, clear)
  }
  return request
}

export const authBaseUrl = `${baseUrl}/api/v1/auth`

export async function authRequest<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${authBaseUrl}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  })
  const payload = (await response.json().catch(() => null)) as {
    message?: string
    data?: T
    error?: { message?: string }
  } | null
  if (!response.ok)
    throw new ApiError(
      payload?.message ?? payload?.error?.message ?? "Unable to continue.",
      response.status
    )
  return (payload?.data ?? payload) as T
}
